import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSLogo,
  MDSLogoKind,
  MDSLogoSize,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  DEFAULT_MEETING_TRIAL_PITCH_TIMINGS,
  type MeetingTrialPitchTimings,
} from "./meeting-trial-pitch-timings";

const MEETING_NOTE_LINES = [
  { copy: "Activation drop-off after calendar step", kind: "prose", skeletonWidth: "88%" },
  { copy: "Agreed to simplify trial screen copy", kind: "prose", skeletonWidth: "70%" },
  { copy: "Send Kevin the activation results", kind: "task", skeletonWidth: "80%" },
  { copy: "Revise the onboarding proposal", kind: "task", skeletonWidth: "74%" },
] as const;

interface MeetingAnimationFrame {
  agentVisible: boolean;
  morningVisible: boolean;
  noteReceded: boolean;
  noteVisible: boolean;
  nudgeVisible: boolean;
  recordingIsComplete: boolean;
  resolvedLineCount: number;
  status: string;
  statusIsResolved: boolean;
  timerSeconds: number;
}

const buildInitialFrame = (timings: MeetingTrialPitchTimings): MeetingAnimationFrame => ({
  agentVisible: false,
  morningVisible: false,
  noteReceded: false,
  noteVisible: false,
  nudgeVisible: false,
  recordingIsComplete: false,
  resolvedLineCount: 0,
  status: "",
  statusIsResolved: false,
  timerSeconds: timings.timerFromSeconds,
});

const buildEndFrame = (timings: MeetingTrialPitchTimings): MeetingAnimationFrame => ({
  agentVisible: true,
  morningVisible: true,
  noteReceded: true,
  noteVisible: true,
  nudgeVisible: true,
  recordingIsComplete: true,
  resolvedLineCount: MEETING_NOTE_LINES.length,
  status: "2 tasks still open",
  statusIsResolved: true,
  timerSeconds: timings.timerToSeconds,
});

const formatTimer = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

export type MeetingTrialAnimationMode = "play" | "end-state";

export interface MeetingFollowThroughAnimationProps {
  mode?: MeetingTrialAnimationMode;
  showReplay?: boolean;
  timings?: MeetingTrialPitchTimings;
}

/**
 * Demonstrates Mem Agent turning meeting notes into persistent follow-through.
 *
 * Reduced-motion users and end-state stories receive the stable final frame
 * immediately. Replay always starts a fresh run instead of resuming a phase.
 */
export const MeetingFollowThroughAnimation = ({
  mode = "play",
  showReplay = true,
  timings = DEFAULT_MEETING_TRIAL_PITCH_TIMINGS,
}: MeetingFollowThroughAnimationProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldRenderEndState = mode === "end-state" || prefersReducedMotion;
  const [runId, setRunId] = useState(0);
  const [frame, setFrame] = useState<MeetingAnimationFrame>(() =>
    shouldRenderEndState ? buildEndFrame(timings) : buildInitialFrame(timings)
  );

  useEffect(() => {
    if (shouldRenderEndState) {
      setFrame(buildEndFrame(timings));
      return;
    }

    const scheduledTimeouts: number[] = [];
    let timerInterval: number | undefined;
    const schedule = (callback: () => void, delayMs: number) => {
      scheduledTimeouts.push(window.setTimeout(callback, delayMs));
    };

    setFrame(buildInitialFrame(timings));
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, noteVisible: true }));
      timerInterval = window.setInterval(() => {
        setFrame(currentFrame => {
          const nextTimerSeconds = Math.min(currentFrame.timerSeconds + 1, timings.timerToSeconds);

          if (nextTimerSeconds === timings.timerToSeconds && timerInterval !== undefined) {
            window.clearInterval(timerInterval);
            timerInterval = undefined;
          }

          return { ...currentFrame, timerSeconds: nextTimerSeconds };
        });
      }, timings.timerTickMs);
    }, timings.noteInMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, recordingIsComplete: true }));
    }, timings.recordingCompleteMs);

    for (let lineIndex = 0; lineIndex < MEETING_NOTE_LINES.length; lineIndex += 1) {
      schedule(
        () => {
          setFrame(currentFrame => ({
            ...currentFrame,
            resolvedLineCount: lineIndex + 1,
          }));
        },
        timings.linesFromMs + lineIndex * timings.lineStaggerMs
      );
    }

    schedule(() => {
      setFrame(currentFrame => ({
        ...currentFrame,
        agentVisible: true,
        noteReceded: true,
      }));
    }, timings.agentInMs);
    schedule(() => {
      setFrame(currentFrame => ({
        ...currentFrame,
        status: "Reviewing latest meeting notes…",
      }));
    }, timings.status1Ms);
    schedule(() => {
      setFrame(currentFrame => ({
        ...currentFrame,
        status: "Reviewing Product Planning…",
      }));
    }, timings.status2Ms);
    schedule(() => {
      setFrame(currentFrame => ({
        ...currentFrame,
        status: "2 tasks still open",
        statusIsResolved: true,
      }));
    }, timings.status3Ms);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, morningVisible: true }));
    }, timings.morningInMs);
    schedule(() => {
      setFrame(currentFrame => ({ ...currentFrame, nudgeVisible: true }));
    }, timings.nudgeInMs);

    return () => {
      for (const timeout of scheduledTimeouts) {
        window.clearTimeout(timeout);
      }

      if (timerInterval !== undefined) {
        window.clearInterval(timerInterval);
      }
    };
  }, [runId, shouldRenderEndState, timings]);

  return (
    <AnimationColumn data-animation-column>
      {!shouldRenderEndState && showReplay ? (
        <ReplayButton
          size={MDSButtonSize.Small}
          variant={MDSButtonVariant.ContrastAccent}
          onPress={() => setRunId(currentRunId => currentRunId + 1)}
        >
          <MDSIcon aria-hidden="true" kind={MDSIconKind.Refresh} size={12} />
          Replay
        </ReplayButton>
      ) : null}

      <Stage aria-label="Mem Agent meeting follow-through example">
        <StageStack>
          <NoteCard
            $isReceded={frame.noteReceded}
            $isVisible={frame.noteVisible}
            $transitionMs={shouldRenderEndState ? 0 : timings.noteTransitionMs}
          >
            <NoteHeader>
              <NoteTitle as="h1" variant={MDSTextVariant.HeaderM} weight={MDSTextWeight.SemiBold}>
                Product Planning — June 17, 2026
              </NoteTitle>
              <RecordingPill
                aria-label={
                  frame.recordingIsComplete
                    ? `Meeting recording ended at ${formatTimer(frame.timerSeconds)}`
                    : `Meeting recording in progress at ${formatTimer(frame.timerSeconds)}`
                }
                role="group"
                $isComplete={frame.recordingIsComplete}
                $transitionMs={shouldRenderEndState ? 0 : timings.recordingSettleTransitionMs}
              >
                <VoiceBars aria-hidden="true">
                  {timings.voiceBars.map((voiceBar, index) => (
                    <VoiceBar
                      // The choreography intentionally gives every bar a distinct cadence.
                      key={`${voiceBar.heightPx}-${index}`}
                      $delayMs={voiceBar.delayMs}
                      $durationMs={voiceBar.durationMs}
                      $heightPx={voiceBar.heightPx}
                      $isAnimated={!shouldRenderEndState && !frame.recordingIsComplete}
                      $isComplete={frame.recordingIsComplete}
                    />
                  ))}
                </VoiceBars>
                <Timer variant={MDSTextVariant.TextXs} weight={MDSTextWeight.Medium}>
                  {formatTimer(frame.timerSeconds)}
                </Timer>
                <PillChevron aria-hidden="true" kind={MDSIconKind.AngleDown} size={10} />
              </RecordingPill>
            </NoteHeader>

            <NoteLines>
              {MEETING_NOTE_LINES.map((line, index) => {
                const isResolved = index < frame.resolvedLineCount;

                return (
                  <NoteLine key={line.copy}>
                    <NoteLineText $isVisible={isResolved} $transitionMs={timings.lineFadeMs}>
                      {line.kind === "task" ? (
                        <MDSIcon aria-hidden="true" kind={MDSIconKind.CheckboxEmpty} size={13} />
                      ) : null}
                      <span>{line.copy}</span>
                    </NoteLineText>
                    <SkeletonLine
                      aria-hidden="true"
                      $isActive={!isResolved}
                      $pulseMs={timings.skeletonPulseMs}
                      $transitionMs={timings.lineFadeMs}
                      $width={line.skeletonWidth}
                    />
                  </NoteLine>
                );
              })}
            </NoteLines>
          </NoteCard>

          <AgentCard
            $isVisible={frame.agentVisible}
            $transitionMs={shouldRenderEndState ? 0 : timings.agentTransitionMs}
          >
            <AgentHeader>
              <AgentHeaderLogo aria-hidden="true" kind={MDSLogoKind.ArrowsCropped} />
              <AgentName>Mem</AgentName>
            </AgentHeader>
            <Status aria-live="polite" $isResolved={frame.statusIsResolved}>
              {frame.status}
            </Status>
            <MorningDivider
              $isVisible={frame.morningVisible}
              $transitionMs={shouldRenderEndState ? 0 : timings.morningFadeMs}
            >
              <DividerLine />
              <span>The next morning</span>
              <DividerLine />
            </MorningDivider>
            <Nudge
              $isVisible={frame.nudgeVisible}
              $transitionMs={shouldRenderEndState ? 0 : timings.nudgeTransitionMs}
            >
              <MessageAvatar aria-hidden="true">
                <MDSLogo kind={MDSLogoKind.LogoCircle} size={MDSLogoSize.Small} />
              </MessageAvatar>
              <NudgeText>
                You still haven&apos;t sent the activation results. Want help finishing them?
              </NudgeText>
            </Nudge>
          </AgentCard>
        </StageStack>
      </Stage>
    </AnimationColumn>
  );
};

const voiceBounce = keyframes`
  0%, 100% { transform: scaleY(.3); }
  50% { transform: scaleY(1); }
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: .55; }
  50% { opacity: 1; }
`;

const AnimationColumn = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 760px) {
    width: 100%;
    max-width: 400px;
    align-self: center;
  }
`;

const Stage = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 440px;
  align-items: center;
  transform: translateY(-20px);

  @media (max-width: 760px) {
    width: 125%;
    height: 352px;
    transform: translateY(-16px) scale(0.8);
    transform-origin: top center;
  }
`;

const StageStack = styled.div`
  position: relative;
  width: 100%;
  height: 290px;
`;

const NoteCard = styled.div<{
  $isReceded: boolean;
  $isVisible: boolean;
  $transitionMs: number;
}>`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  transform: ${({ $isReceded, $isVisible }) => {
    if ($isReceded) return "translateY(0) scale(.9)";
    return $isVisible ? "translateY(0)" : "translateY(10px)";
  }};
  transform-origin: top center;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
  opacity: ${({ $isReceded, $isVisible }) => {
    if ($isReceded) return 0.5;
    return $isVisible ? 1 : 0;
  }};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const NoteTitle = styled(MDSText)`
  display: block;
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
`;

const RecordingPill = styled.div<{ $isComplete: boolean; $transitionMs: number }>`
  display: flex;
  height: 28px;
  align-items: center;
  flex: none;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: 0 ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ $isComplete, theme }) =>
    $isComplete
      ? theme.colors.background.button.subtle.default
      : theme.colors.background.button.emphasisLight.default};
  opacity: ${({ $isComplete }) => ($isComplete ? 0.58 : 1)};
  transition:
    background-color ${({ $transitionMs }) => $transitionMs}ms ease,
    opacity ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const VoiceBars = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 14px;
`;

const VoiceBar = styled.span<{
  $delayMs: number;
  $durationMs: number;
  $heightPx: number;
  $isAnimated: boolean;
  $isComplete: boolean;
}>`
  width: ${({ $isComplete }) => ($isComplete ? 4 : 2)}px;
  height: ${({ $heightPx, $isComplete }) => ($isComplete ? 4 : $heightPx)}px;
  transform-origin: center;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ theme }) => theme.colors.icon.emphasis};
  transition:
    width 180ms ease,
    height 180ms ease;
  animation: ${({ $delayMs, $durationMs, $isAnimated }) =>
    $isAnimated
      ? css`
          ${voiceBounce} ${$durationMs}ms infinite ${$delayMs}ms
        `
      : "none"};
`;

const Timer = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.emphasis};
  white-space: nowrap;
`;

const PillChevron = styled(MDSIcon)`
  color: ${({ theme }) => theme.colors.icon.emphasis};
`;

const NoteLines = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoteLine = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing[1]} 0;
`;

const NoteLineText = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;

  .mds-icon-wrapper {
    flex: none;
    margin-top: 1px;
  }
`;

const SkeletonLine = styled.span<{
  $isActive: boolean;
  $pulseMs: number;
  $transitionMs: number;
  $width: string;
}>`
  position: absolute;
  top: 9px;
  left: 0;
  width: ${({ $width }) => $width};
  height: 10px;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ theme }) => theme.colors.background.button.default.default};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;

  /* Stopping the pulse is required: a running CSS animation overrides a
     separately assigned opacity and makes resolved skeletons reappear. */
  animation: ${({ $isActive, $pulseMs }) =>
    $isActive
      ? css`
          ${skeletonPulse} ${$pulseMs}ms infinite
        `
      : "none"};
`;

const AgentCard = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  position: absolute;
  top: 56px;
  right: 0;
  left: 0;
  min-height: 210px;
  box-sizing: border-box;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(28px)")};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.chat};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const AgentHeaderLogo = styled(MDSLogo)`
  height: 9px;
`;

const AgentName = styled.span`
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: ${({ theme }) => theme.fontSizes["text-m"]};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  line-height: ${({ theme }) => theme.lineHeights["text-m"]};
`;

const Status = styled.p<{ $isResolved: boolean }>`
  min-height: ${({ theme }) => theme.lineHeights["text-s"]};
  margin: 0 0 ${({ theme }) => theme.spacing[1]};
  color: ${({ $isResolved, theme }) =>
    $isResolved ? theme.colors.text.secondary : theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
`;

const MorningDivider = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin: ${({ theme }) => `${theme.spacing[4]} 0`};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const DividerLine = styled.span`
  height: 1px;
  flex: 1;
  background: ${({ theme }) => theme.colors.text.tertiary};
  opacity: 0.55;
`;

const Nudge = styled.div<{ $isVisible: boolean; $transitionMs: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  transform: ${({ $isVisible }) => ($isVisible ? "translateY(0)" : "translateY(10px)")};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition:
    opacity ${({ $transitionMs }) => $transitionMs}ms ease,
    transform ${({ $transitionMs }) => $transitionMs}ms ease;
`;

const MessageAvatar = styled.span`
  display: flex;
  width: 24px;
  height: 24px;
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.max};
`;

const NudgeText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
`;

const ReplayButton = styled(MDSButton)`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  min-height: 28px;
  transform: translateY(-4px);
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 160ms ease,
    transform 160ms ease;

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.secondary};
  }

  [data-animation-column]:hover &,
  [data-animation-column]:focus-within &,
  &:focus-visible {
    transform: translateY(0);
    opacity: 0.82;
    pointer-events: auto;
  }

  &:hover,
  &[data-hovered]:not([data-disabled]) {
    opacity: 1;
  }
`;
